import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../../api/client';
import { ExportMenu } from '../../../components/AppToolbar/ExportMenu';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid/AppDataGrid';
import { AppDialog } from '../../../components/AppDialog';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow, useDeleteFlow } from '../../../components/confirm';
import { fmtLocalDateTime } from '../../../utils/datetime';
import './preLoginSettings.css';
import type { PreLoginAttachment, PreLoginNote } from './types';

interface Props {
  /** `null` while the record is NEW and not yet saved — the panel renders its
   *  tabs and empty grids rather than disappearing, so the form does not change
   *  shape the first time you save (owner request 2026-09-28). */
  cocOid: string | null;
  /** Counts from the header read, so the tab captions match the legacy screen
   *  without this panel having to fetch both lists up front. */
  attachmentCount?: number;
  noteCount?: number;
  /** Which record type owns the rows. Pre-Login Settings when omitted; a second
   *  owner (the Open Quote detail, 2026-10-13) passes its own instead of
   *  forking this panel (standing rule 8). */
  owner?: DocsOwner;
  /** The host draws the tab strip itself (the Open Quote form puts these two
   *  tabs in the same strip as its pricing tabs, as legacy does). The panel
   *  then renders no card and no tabs of its own, only the active tab's body. */
  embedded?: boolean;
  /** Which tab to show when `embedded`. */
  activeTab?: DocTab;
  /** Row counts, reported whenever the lists change, for the host's captions. */
  onCounts?: (counts: { attachments: number; notes: number }) => void;
  /** OPT-IN: view only — no New / Edit / Delete and no cell editing (Export and
   *  the file links stay). First use: a validated quote, which legacy shows with
   *  CollectionsEditMode="View". Unset = unchanged. */
  readOnly?: boolean;
  /** OPT-IN, used with `readOnly`: HIDE the New / Add / Edit / Delete buttons instead
   *  of leaving them greyed (Module Access Control — a Read-only role must not see
   *  write actions). Unset = unchanged (the other owners keep their greyed bar). */
  hideWriteActions?: boolean;
}

/** What differs between two owners of this panel — nothing else does. */
export interface DocsOwner {
  /** API root that serves `{id}/attachments`, `{id}/notes` and
   *  `attachments/file/...`, with the response shapes this panel reads. */
  apiBase: string;
  /** Form name for the shared save / delete flows — the reason gate and the
   *  audit log are keyed on it (standing rule 11). */
  formName: string;
  /** Shown read-only as the new note's "Business Object". */
  businessObject: string;
  /** Prefix of the two grids' export file names. */
  exportPrefix: string;
  /** Caption of the second tab. Legacy says "Note" on some screens and "Notes"
   *  on others. */
  noteTabLabel: string;
  /** True when the owner's header read supplies no counts, so the captions
   *  count the loaded rows instead. */
  countFromRows?: boolean;
  /** OPT-IN legacy XAF nested-list layout (first use: Open Quote, owner
   *  screenshot 2026-10-20): the bar reads New · Edit · Delete · Export to
   *  instead of Add · Delete · Refresh, Edit opens the record in the same popup
   *  as New, and the Notes grid shows legacy's columns (Title · Attachment ·
   *  Note · Author · Date · IsCaseNarrative · sort). Unset = unchanged. */
  legacyListBar?: boolean;
}

const PRE_LOGIN: DocsOwner = {
  apiBase: '/api/v1/pre-login-settings',
  formName: 'Pre-Login Settings',
  businessObject: 'Pre-Login Settings',
  exportPrefix: 'Pre-Login',
  noteTabLabel: 'Note',
};

/** A file link cannot be a plain `<a href>`: the API wants the bearer token,
 *  which a browser navigation does not send, so the file is fetched through the
 *  shared client and handed to the browser as a blob. */
async function downloadFile(apiBase: string, fileOid: string, fileName: string | null) {
  const res = await api.get<Blob>(`${apiBase}/attachments/file/${fileOid}/download`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName ?? 'attachment';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked later: revoking at once can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export type DocTab = 'attachment' | 'note';

/** The legacy int enum, in its stored order (0..5). Sent as the INDEX, because
 *  `dbo.Attachment.Category` is an int and the read SP maps it back to text. */
const CATEGORIES = ['COC', 'Image', 'Pdf Report', 'Result EDD', 'Subout', 'Other'];

/**
 * The **Attachment | Note** tabbed control at the bottom of the Pre-Login
 * detail — the same place and shape the legacy XAF screen puts it.
 *
 * <p>Both are SHARED, MULTI-PARENT legacy tables: `dbo.Attachment` and
 * `dbo.Notes` each carry one FK column per owning business object, and only
 * `COCSettings` scopes them to this record. Every write touches that column
 * alone, so a row can never be stolen from another owner.</p>
 *
 * <p><b>EDITABLE since 2026-09-28</b> (owner: "attachments and notes should not
 * be read only ... should have add, delete button and edit in the grid cell").
 * Each grid has a selection checkbox column, **Add** and **Delete** in its own
 * section toolbar, and click-to-edit cells for the free-text fields — Add opens
 * a small dialog rather than inserting a blank row, because both records need
 * at least one value (a note its title, an attachment its file) and a row that
 * exists only to be filled in is a row someone will leave empty.</p>
 *
 * <p><b>Notes are author-only</b> (CLAUDE.md §14a Rule C): only the user who
 * wrote a note may edit or delete it. That is enforced in the write SPs, which
 * return the single canonical message as a 403. What happens HERE is only the
 * mirror of it — the per-row `canEdit` the read computes hides the actions from
 * a non-author. Never treat this flag as the rule. Attachments carry no such
 * rule: legacy has one on Notes only.</p>
 */
export default function PreLoginDocsPanel({
  cocOid, attachmentCount = 0, noteCount = 0, owner = PRE_LOGIN,
  embedded = false, activeTab, onCounts, readOnly = false, hideWriteActions = false,
}: Props) {
  const { apiBase, formName } = owner;
  const notify = useNotify();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();

  const [ownTab, setTab] = useState<DocTab>('attachment');
  const tab = embedded && activeTab ? activeTab : ownTab;
  const [attachments, setAttachments] = useState<PreLoginAttachment[]>([]);
  const [notes, setNotes] = useState<PreLoginNote[]>([]);
  const [loading, setLoading] = useState(false);
  /** True once the rows have been read; the tab captions then count the live rows,
   *  so a delete / add shows at once instead of the header's stale count. */
  const [loaded, setLoaded] = useState(false);
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  const [adding, setAdding] = useState(false);
  /** The row being edited in the dialog (Edit button) - null = adding a new one. */
  const [editing, setEditing] = useState<PreLoginAttachment | PreLoginNote | null>(null);

  const load = useCallback(async () => {
    // Nothing can hang off a record that has no Oid yet, and asking the API for
    // `/pre-login-settings/null/attachments` would surface a 404 as a failure
    // banner on a form the user has not done anything wrong on.
    if (!cocOid) { setAttachments([]); setNotes([]); return; }
    setLoading(true);
    try {
      const [a, n] = await Promise.all([
        api.get(`${apiBase}/${cocOid}/attachments`),
        api.get(`${apiBase}/${cocOid}/notes`),
      ]);
      setAttachments(a.data?.data ?? []);
      setNotes(n.data?.data ?? []);
      setLoaded(true);
    } catch {
      notify.error('Could not load the attachments and notes.');
    } finally {
      setLoading(false);
    }
  }, [cocOid, notify, apiBase]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    onCounts?.({ attachments: attachments.length, notes: notes.length });
  }, [attachments.length, notes.length, onCounts]);
  // Selection is per tab: a ticked note must not be deletable from the
  // Attachment tab's Delete button.
  useEffect(() => { setSel(new Set()); }, [tab]);

  /** One cell edit = one PUT of that row. The grid commits on blur/Enter, so
   *  this fires once per finished edit rather than per keystroke. */
  const commitCell = useCallback(async (
    kind: DocTab, oid: string, patch: Record<string, unknown>,
  ) => {
    if (!cocOid) return;
    const path = kind === 'note' ? 'notes' : 'attachments';
    try {
      const res = await api.put(`${apiBase}/${cocOid}/${path}/${oid}`, patch);
      if (kind === 'note') setNotes(res.data?.data ?? []);
      else setAttachments(res.data?.data ?? []);
      notify.success('Saved successfully.');
    } catch (err) {
      // A 403 here is the author rule, and its message is the one to show.
      const e = err as { response?: { data?: { error?: { message?: string } } } };
      notify.error(e.response?.data?.error?.message ?? 'Could not save the change.');
      void load();   // put the cell back to what the server actually holds
    }
  }, [cocOid, notify, load, apiBase]);

  const deleteSelected = useCallback(() => {
    if (!cocOid || sel.size === 0) return;
    // Notes are author-only on delete as well as edit (§14a Rule C). The server
    // refuses with 403 regardless; telling the user BEFORE the confirm mirrors
    // what Edit already does, so a ticked foreign note is not half-processed.
    if (tab === 'note' && notes.some((n) => sel.has(n.oid) && !n.canEdit)) {
      notify.warning('You are not authorized to modify a note created by another author.');
      return;
    }
    const path = tab === 'note' ? 'notes' : 'attachments';
    void runDelete({
      formName,
      message: `Delete ${sel.size} ${tab === 'note' ? 'note' : 'attachment'}${sel.size === 1 ? '' : 's'}?`,
      delete: async () => {
        // Sequential on purpose: each delete is audited against the COC, and the
        // author rule can refuse one row of a selection without the rest being
        // wrong. A failure stops the run and reports which row refused.
        for (const oid of sel) {
          await api.delete(`${apiBase}/${cocOid}/${path}/${String(oid)}`);
        }
      },
      onDeleted: () => { setSel(new Set()); void load(); },
    });
  }, [cocOid, sel, tab, notes, notify, runDelete, load, apiBase, formName]);

  /** Edit: one ticked row opens the same dialog, pre-filled. Notes are author-only
   *  (the server enforces it; the per-row canEdit mirrors it so the user is told
   *  up front rather than after typing). */
  const editSelected = useCallback(() => {
    if (sel.size !== 1) { notify.warning('Select one row to edit.'); return; }
    const id = String([...sel][0]);
    if (tab === 'note') {
      const r = notes.find(n => n.oid === id);
      if (!r) return;
      if (!r.canEdit) { notify.warning('You are not authorized to modify a note created by another author.'); return; }
      setEditing(r);
    } else {
      const r = attachments.find(a => a.oid === id);
      if (r) setEditing(r);
    }
  }, [sel, tab, notes, attachments, notify]);

  const fileLink = useCallback((fileOid: string, fileName: string | null) => (
    <a href="#" onClick={(e) => {
      e.preventDefault();
      downloadFile(apiBase, fileOid, fileName)
        .catch(() => notify.error('Could not download the file.'));
    }}>{fileName ?? 'Download'}</a>
  ), [apiBase, notify]);

  /** Legacy Attachment grid: Name · Category · Attachment · Date · Operator.
   *  Name / Category / Comment are click-to-edit; Attachment, Date and Operator
   *  are server-owned (the file, and who filed it when) and stay read-only. */
  const attachmentCols: AppColumn<PreLoginAttachment>[] = useMemo(() => [
    { field: 'name', header: 'Name', width: 180, edit: { type: 'text' } },
    { field: 'category', header: 'Category', width: 140,
      edit: { type: 'combo', options: CATEGORIES } },
    { field: 'attachment', header: 'Attachment', width: 180,
      render: (r: PreLoginAttachment) => r.attachmentOid
        ? fileLink(r.attachmentOid, r.attachment)
        : <span style={{ color: 'var(--ai-muted)' }}>—</span> },
    { field: 'date', header: 'Date', width: 150, type: 'datetime' },
    { field: 'operator', header: 'Operator', width: 150 },
    { field: 'comment', header: 'Comment', width: 200, edit: { type: 'text' } },
  ], [fileLink]);

  /** BO UI Standard v4 §8 Project Notes columns. Title and Note are
   *  click-to-edit FOR THE AUTHOR ONLY — `disabled` mirrors the server rule. */
  const noteCols: AppColumn<PreLoginNote>[] = useMemo(() => owner.legacyListBar ? [
    // Legacy CRMQuotes_Note_ListView, in its order.
    { field: 'title', header: 'Title', width: 160,
      edit: { type: 'text', disabled: (r: PreLoginNote) => !r.canEdit } },
    { field: 'attachment', header: 'Attachment', width: 160,
      render: (r: PreLoginNote) => r.attachmentOid
        ? fileLink(r.attachmentOid, r.attachment)
        : <span style={{ color: 'var(--ai-muted)' }}>—</span> },
    { field: 'note', header: 'Note', width: 260, wrap: true,
      edit: { type: 'text', disabled: (r: PreLoginNote) => !r.canEdit } },
    { field: 'author', header: 'Author', width: 140 },
    { field: 'date', header: 'Date', width: 150, type: 'datetime' },
    { field: 'isCaseNarrative', header: 'IsCaseNarrative', width: 120, align: 'center',
      render: (r: PreLoginNote) => <input type="checkbox" checked={!!r.isCaseNarrative} readOnly disabled aria-label="IsCaseNarrative" /> },
    { field: 'sort', header: 'sort', width: 80, align: 'right', type: 'number' },
  ] : [
    { field: 'title', header: 'Title', width: 160,
      edit: { type: 'text', disabled: (r: PreLoginNote) => !r.canEdit } },
    { field: 'businessObject', header: 'BusinessObject', width: 150 },
    { field: 'sourceId', header: 'SourceID', width: 110 },
    { field: 'note', header: 'Note', width: 260, wrap: true,
      edit: { type: 'text', disabled: (r: PreLoginNote) => !r.canEdit } },
    { field: 'author', header: 'Author', width: 140 },
    { field: 'date', header: 'Date', width: 150, type: 'datetime' },
    // The note's own OPTIONAL file (dbo.Notes.Attachment, Notes.cs) — distinct
    // from the Attachment tab's rows.
    { field: 'attachment', header: 'Attachment', width: 160,
      render: (r: PreLoginNote) => r.attachmentOid
        ? fileLink(r.attachmentOid, r.attachment)
        : <span style={{ color: 'var(--ai-muted)' }}>—</span> },
  ], [fileLink, owner.legacyListBar]);

  const canWrite = !!cocOid && !readOnly;
  // Edit (legacy bar): the ONE ticked row, opened in the New popup. A note
  // another user wrote cannot be edited (§14a Rule C) — mirrored here, enforced
  // by the server. (`editing` itself is declared above, shared with Pre-Login's
  // own Edit button.)
  const selectedRow = sel.size === 1
    ? (tab === 'note' ? notes : attachments).find((r) => r.oid === String([...sel][0])) ?? null
    : null;
  const canEditSelected = !!selectedRow && (tab !== 'note' || (selectedRow as PreLoginNote).canEdit);

  return (
    <section className={embedded ? undefined : 'sr-sec'}>
      <div className={embedded ? undefined : 'sr-sec-b'} style={embedded ? undefined : { padding: 8 }}>
        {/* The shared in-form TAB STRIP (`.sr-tabbar` / `.sr-tb`), the same
            control Sample Registration uses for its bottom tabs and its Sample
            Login tabs — not the `.sa-filter-btn` pills this used until
            2026-09-28. These are tabs switching between two views of the record,
            which is what a tab strip means; the orange pills read as filter
            chips narrowing one list, which is what they mean elsewhere. */}
        {!embedded && <div className="sr-tabbar" role="tablist">
          {([
            ['attachment', `Attachment (${owner.countFromRows || loaded ? attachments.length : attachmentCount})`],
            ['note',       `${owner.noteTabLabel} (${owner.countFromRows || loaded ? notes.length : noteCount})`],
          ] as const).map(([id, label]) => (
            <button key={id} role="tab" aria-selected={tab === id}
                    className={`sr-tb${tab === id ? ' active' : ''}`}
                    onClick={() => setTab(id)}>{label}</button>
          ))}
        </div>}

        {/* Section toolbar — v4 §6 middle altitude: a child grid's actions are
            its own, never global. */}
        {owner.legacyListBar ? (
        <div className="pl-sectionbar">
          {!(readOnly && hideWriteActions) && (<>
          <button className="ca-btn" disabled={!canWrite} onClick={() => setAdding(true)}>
            <i className="ti ti-file-plus tb-i-green" /> New
          </button>
          <button className="ca-btn" disabled={!canWrite || !canEditSelected}
                  onClick={() => setEditing(selectedRow)}>
            <i className="ti ti-pencil tb-i-blue" /> Edit
          </button>
          <button className="ca-btn" disabled={!canWrite || sel.size === 0} onClick={deleteSelected}>
            <i className="ti ti-x tb-i-red" /> Delete
          </button>
          </>)}
          {tab === 'note'
            ? <ExportMenu filename={`${owner.exportPrefix} Notes`} {...gridExportMatrix(notes, noteCols)} triggerClassName="ca-btn" />
            : <ExportMenu filename={`${owner.exportPrefix} Attachments`} {...gridExportMatrix(attachments, attachmentCols)} triggerClassName="ca-btn" />}
          <span className="pl-sectionbar-spacer" />
          {!cocOid && <span className="sr-sec-note">save the record to add {tab === 'note' ? 'notes' : 'attachments'}</span>}
        </div>
        ) : (
        <div className="pl-sectionbar">
          {!(readOnly && hideWriteActions) && (<>
          <button className="ca-btn" disabled={!canWrite} onClick={() => setAdding(true)}>
            <i className="ti ti-plus tb-i-green" /> Add
          </button>
          <button className="ca-btn" disabled={!canWrite || sel.size !== 1} onClick={editSelected}>
            <i className="ti ti-edit tb-i-blue" /> Edit
          </button>
          <button className="ca-btn" disabled={!canWrite || sel.size === 0} onClick={deleteSelected}>
            <i className="ti ti-trash tb-i-red" /> Delete
          </button>
          </>)}
          <button className="ca-btn" onClick={() => void load()}>
            <i className="ti ti-refresh tb-i-green" /> Refresh
          </button>
          <span className="pl-sectionbar-spacer" />
          {!cocOid && <span className="sr-sec-note">save the record to add {tab === 'note' ? 'notes' : 'attachments'}</span>}
        </div>
        )}

        {tab === 'attachment' ? (
          <AppDataGrid<PreLoginAttachment>
            rows={attachments}
            columns={attachmentCols}
            getRowId={r => r.oid}
            loading={loading}
            selectable
            selectedIds={sel}
            onSelectionChange={setSel}
            onCellCommit={readOnly ? undefined : (row, col, value) =>
              void commitCell('attachment', row.oid, {
                name:     col.field === 'name' ? value : row.name,
                category: CATEGORIES.indexOf(col.field === 'category' ? value : (row.category ?? '')),
                comment:  col.field === 'comment' ? value : row.comment,
                // null = keep the current file; the cell editor never changes it.
                fileDataOid: null,
              })}
            defaultPageSize={10}
            maximizable
            hideHeaderExport={owner.legacyListBar}
            exportFilename={`${owner.exportPrefix} Attachments`}
            emptyText="No data to display"
          />
        ) : (
          <AppDataGrid<PreLoginNote>
            rows={notes}
            columns={noteCols}
            getRowId={r => r.oid}
            loading={loading}
            selectable
            selectedIds={sel}
            onSelectionChange={setSel}
            onCellCommit={readOnly ? undefined : (row, col, value) =>
              void commitCell('note', row.oid, {
                title: col.field === 'title' ? value : row.title,
                text:  col.field === 'note'  ? value : row.note,
              })}
            defaultPageSize={10}
            maximizable
            hideHeaderExport={owner.legacyListBar}
            exportFilename={`${owner.exportPrefix} Notes`}
            emptyText="No data to display"
          />
        )}

        {(adding || editing) && cocOid && (
          <AddDocDialog
            kind={tab}
            cocOid={cocOid}
            owner={owner}
            row={editing}
            onClose={() => { setAdding(false); setEditing(null); }}
            onAdded={() => { setAdding(false); setEditing(null); setSel(new Set()); void load(); }}
            runSave={runSave}
          />
        )}
      </div>
    </section>
  );
}

interface AddProps {
  kind: DocTab;
  /** Set when editing an existing row; null/undefined = add. */
  row?: PreLoginAttachment | PreLoginNote | null;
  cocOid: string;
  owner: DocsOwner;
  onClose: () => void;
  onAdded: () => void;
  runSave: ReturnType<typeof useSaveFlow>;
}

/**
 * **Add** — one dialog for both tabs, because they differ only in their fields.
 *
 * <p>An attachment (or a note's own optional file — `dbo.Notes.Attachment`,
 * `Notes.cs`) uploads its file FIRST and saves the returned `dbo.FileData` Oid
 * with the row. That is the two-step the Instrument Attachment form uses, and
 * it is the right way round: a failed save leaves an unreferenced blob, which
 * is harmless, where the reverse would leave a row pointing at nothing.</p>
 *
 * <p><b>Realigned 2026-09-29</b> (owner report on the freshly-built popup):
 * both fields used to sit inside the wider 4-column `.sr-fgrid`, so with only
 * one pair each (Title/Note, then a lone Name/Category pair) they crammed
 * onto a single row — Note's label landing beside Title's input, its textarea
 * squeezed into one ~140px column. `.pl-add-grid` here is sized for THIS
 * dialog's own field count instead. Also adds the legacy read-only Author /
 * Date (Notes.cs) and Date / Operator (Attachment.cs) fields the first cut
 * left out, and a Note's own optional Attachment file.</p>
 */
function AddDocDialog({ kind, cocOid, owner, row, onClose, onAdded, runSave }: AddProps) {
  const { apiBase } = owner;
  const notify = useNotify();
  const noteRow = row && kind === 'note' ? (row as PreLoginNote) : null;
  const attRow = row && kind === 'attachment' ? (row as PreLoginAttachment) : null;
  const [title, setTitle] = useState(noteRow?.title ?? '');
  const [text, setText] = useState(noteRow?.note ?? '');
  const [noteFile, setNoteFile] = useState<File | null>(null);
  const [name, setName] = useState(attRow?.name ?? '');
  const [category, setCategory] = useState(
    attRow ? Math.max(0, CATEGORIES.indexOf(attRow.category ?? '')) : 0);
  const [comment, setComment] = useState((attRow as { comment?: string | null } | null)?.comment ?? '');
  const [file, setFile] = useState<File | null>(null);
  /** The Name the last file pick filled in, so a later pick may replace it but a
   *  Name the user typed or changed is never overwritten. */
  const [autoName, setAutoName] = useState('');

  /** ALPACA fills Name from the chosen file's name (without its extension). */
  const pickAttachment = (f: File | null) => {
    setFile(f);
    if (!f) return;
    const base = f.name.replace(/\.[^.]*$/, '');
    if (!name.trim() || name === autoName) { setName(base); setAutoName(base); }
  };

  // The Author / Operator and Date shown read-only in the popup are the REAL
  // values (legacy fills them when the object is constructed): the signed-in
  // employee's name - the same First + Last the grid's Author / Operator column
  // shows - and the moment the popup opened. The server still stamps its own on
  // save, so these are informational and cannot be forged.
  const [who, setWho] = useState('');
  // Built from LOCAL parts as a naive timestamp: while UTC conversion is switched off
  // (ADR-0008) fmtLocal* shows a string's literal wall-clock, so a toISOString() "Z"
  // value would display the UTC hour instead of the user's.
  const [openedAt] = useState(() => {
    const n = new Date();
    const p2 = (v: number) => String(v).padStart(2, '0');
    return fmtLocalDateTime(
      `${n.getFullYear()}-${p2(n.getMonth() + 1)}-${p2(n.getDate())}T${p2(n.getHours())}:${p2(n.getMinutes())}:00`);
  });
  useEffect(() => {
    let alive = true;
    api.get('/api/v1/my-details')
      .then(r => {
        const d = r.data?.data as { firstName?: string | null; lastName?: string | null; displayName?: string } | undefined;
        const full = `${d?.firstName ?? ''} ${d?.lastName ?? ''}`.trim();
        if (alive) setWho(full || d?.displayName || '');
      })
      .catch(() => { /* informational only - the server stamps the real value */ });
    return () => { alive = false; };
  }, []);

  const uploadFile = async (f: File): Promise<string | null> => {
    const fd = new FormData();
    fd.append('file', f);
    const up = await api.post(`${apiBase}/attachments/file/upload`, fd);
    return up.data?.data?.oid ?? null;
  };

  const submit = () => {
    if (kind === 'note' && !title.trim()) { notify.error("'Title' must not be empty."); return; }
    if (kind === 'attachment' && !name.trim()) { notify.error("'Name' must not be empty."); return; }
    if (kind === 'attachment' && !file && !attRow) { notify.error("'Attachment' must not be empty."); return; }

    void runSave({
      formName: owner.formName,
      auditAction: row ? 'edit' : 'create',
      save: async () => {
        // null FileDataOid on an edit = keep the current file (writer convention).
        if (kind === 'note') {
          const fileDataOid = noteFile ? await uploadFile(noteFile) : null;
          const body = { title, text, fileDataOid };
          if (noteRow) await api.put(`${apiBase}/${cocOid}/notes/${noteRow.oid}`, body);
          else await api.post(`${apiBase}/${cocOid}/notes`, body);
          return;
        }
        // null file on an edit = keep the current one (the write SP's rule).
        const fileDataOid = file ? await uploadFile(file) : null;
        const body = { name, category, fileDataOid, comment };
        if (attRow) await api.put(`${apiBase}/${cocOid}/attachments/${attRow.oid}`, body);
        else await api.post(`${apiBase}/${cocOid}/attachments`, body);
      },
      onSaved: onAdded,
    });
  };

  return (
    <AppDialog
      open
      onClose={onClose}
      title={`${row ? 'Edit' : 'New'} ${kind === 'note' ? 'Note' : 'Attachment'}`}
      icon={kind === 'note' ? 'ti-notes' : 'ti-paperclip'}
      width={520}
      footer={<>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
        <button className="ca-btn ca-btn-solid" onClick={submit}>
          <i className="ti ti-device-floppy" /> Save
        </button>
      </>}
    >
      {kind === 'note' ? (
        <>
          <div className="pl-add-grid">
            <label className="req">Title</label>
            <input className="sr-input" value={title} onChange={e => setTitle(e.target.value)} />
            <label>Business Object</label>
            <input readOnly value={owner.businessObject} />

            <label>Author</label>
            <input readOnly value={who} />
            <label>Date</label>
            <input readOnly value={openedAt} />
          </div>
          <label className="pl-add-label">Note</label>
          <textarea className="pl-add-note" rows={5} value={text} onChange={e => setText(e.target.value)} />
          <label className="pl-add-label">Attachment (optional)</label>
          <input type="file" onChange={e => setNoteFile(e.target.files?.[0] ?? null)} />
        </>
      ) : (
        <div className="pl-add-grid">
          <label className="req">Name</label>
          <input className="sr-input" value={name} onChange={e => setName(e.target.value)} />
          <label>Category</label>
          <select className="sr-input" value={category}
                  onChange={e => setCategory(Number(e.target.value))}>
            {CATEGORIES.map((c, i) => <option key={c} value={i}>{c}</option>)}
          </select>

          <label className={attRow ? undefined : 'req'}>Attachment</label>
          <input type="file" onChange={e => pickAttachment(e.target.files?.[0] ?? null)} />
          <label>Comment</label>
          <input className="sr-input" value={comment} onChange={e => setComment(e.target.value)} />

          <label>Date</label>
          <input readOnly value={openedAt} />
          <label>Operator</label>
          <input readOnly value={who} />
        </div>
      )}
    </AppDialog>
  );
}
